import type { zhCN } from './zh-CN.ts';

/**
 * English dictionary — mirrors `zh-CN.ts`'s key space exactly via the
 * `Record<keyof typeof zhCN, string>` annotation. Removing or misnaming
 * a key here is a type error. Adding a new key: update `zh-CN.ts` first;
 * TS will then require the same key here.
 *
 * Value type is widened to `string` (not the literal-string type
 * `typeof zhCN` produces under `as const`) so each locale can carry its
 * own copy without the compiler demanding the Chinese phrase verbatim.
 * The key-space guarantee is the contract we need; the value is free-
 * form by design.
 *
 * Copy style
 * - Sentence case for user-facing text (not Title Case).
 * - Prefer imperative verbs in buttons ("Get started", not "Click here").
 * - Keep marketing copy tight and developer-oriented — no "revolutionary"
 *   or "game-changing" language. The audience is technical.
 */
export const en: Record<keyof typeof zhCN, string> = {
  // ── <head> metadata ───────────────────────────────────────────
  'meta.title': 'nadoutong — AI Model Gateway | Claude · GPT Direct Access',
  'meta.description':
    'One API key for Claude and GPT models. Direct backbone routing from within China, per-token CNY billing, and native Anthropic Messages API compatibility today.',

  // ── Header ─────────────────────────────────────────────────────
  'header.nav.models': 'Models',
  'header.nav.pricing': 'Pricing',
  'header.nav.status': 'Status',
  'header.nav.tools': 'Tools',
  'header.nav.docs': 'Docs',
  'header.cta.sign_in': 'Sign in',
  'header.cta.get_started': 'Get started',

  // ── Hero ───────────────────────────────────────────────────────
  'hero.title.line1': 'Frontier models from abroad,',
  'hero.title.accent': 'one API key',
  'hero.title.line3': 'for developers in China.',
  'hero.subtitle':
    'One API key for Claude and GPT models. Backbone-grade routing with millisecond responses, per-token CNY billing, and native Anthropic Messages API compatibility today.',
  'hero.cta.primary': 'Create free account',
  'hero.cta.secondary': 'Read the docs',
  'hero.trust.no_proxy': 'No proxy required',
  'hero.trust.no_expiry': 'Balance never expires',
  'hero.trust.pay_as_you_go': 'Per-token billing in real time',

  // ── Hero · Terminal ───────────────────────────────────────────
  'hero.terminal.filename': '~/my-app — curl',
  'hero.terminal.p50': 'p50 240ms',
  'hero.terminal.comment': "# swap Anthropic SDK's base URL and you're done",
  'hero.terminal.streaming_label': '→ streaming',
  'hero.terminal.response':
    "Happy to help. Here's an async rate limiter built on a token bucket",
  'hero.terminal.meta.model': 'model',
  'hero.terminal.meta.tokens': 'tokens: {in} in / {out} out',
  'hero.terminal.meta.cost': '¥ {cost}',
  'hero.terminal.caption.base_url': 'Swap the Base URL · keep your SDK',

  // ── Provider row ──────────────────────────────────────────────
  'providers.openai': 'OpenAI',
  'providers.anthropic': 'Anthropic',
  'providers.gemini': 'Google Gemini',
  'providers.coming_soon': 'Coming soon',

  // ── Features ──────────────────────────────────────────────────
  'features.eyebrow': 'Why us',
  'features.title.lead': 'Stable, fast, ',
  'features.title.accent': 'effortless.',
  'features.subtitle':
    "We're not building a mirror — we're building the AI API infrastructure that developers in mainland China actually expect.",
  'features.backbone.title': 'Direct backbone routing',
  'features.backbone.body':
    'Self-hosted BGP ingress across three CN2 backbones with global PoPs. Reach Claude and GPT models without a proxy.',
  'features.backbone.meta': 'p50 < 300ms · p95 < 900ms',
  'features.billing.title': 'Transparent CNY billing',
  'features.billing.body':
    'Metered per-token deductions. No tiers, no mandatory prepay, no surprises. Pay via WeChat Pay.',
  'features.billing.meta': 'Balance never expires',
  'features.sdk.title': 'Native Anthropic API',
  'features.sdk.body':
    'Preserves the Anthropic Messages API request and response shape, so existing Anthropic clients only need a Base URL swap. OpenAI Chat Completions and Gemini ingress remain on the roadmap.',
  'features.sdk.meta': 'Today /v1/messages · /v1/models',

  // ── Tools ─────────────────────────────────────────────────────
  'tools.eyebrow': 'Ready to plug in',
  'tools.title.lead': 'Built for your ',
  'tools.title.accent': 'workflow',
  'tools.title.trail': '',
  'tools.subtitle':
    'Every mainstream AI editor, agent and CLI. Swap one environment variable and your traffic routes through our gateway.',
  'tools.claude_code': 'Claude Code',
  'tools.cursor': 'Cursor',
  'tools.vscode': 'VS Code',
  'tools.opencode': 'OpenCode',
  'tools.jetbrains': 'JetBrains',
  'tools.openclaw': 'OpenClaw',
  'tools.footnote':
    'Also supports Codex · Continue · Aider · Zed · Cline · LibreChat · OpenWebUI',
  'tools.footnote.link': 'see the full list',

  // ── Models ────────────────────────────────────────────────────
  'models.eyebrow': 'Model matrix',
  'models.title.lead': "Today's strongest ",
  'models.title.accent': 'model capabilities',
  'models.tag.flagship': 'Flagship',
  'models.tag.balanced': 'Balanced',
  'models.tag.fast': 'Fast',
  'models.tag.code': 'Code',

  // ── Pricing ───────────────────────────────────────────────────
  'pricing.eyebrow': 'Transparent pricing',
  'pricing.title.accent': 'Pay-as-you-go',
  'pricing.title.trail': ', in CNY',
  'pricing.subtitle':
    'Per-token billing in real time. Prices are the upstream list price converted at the published FX rate — no markup, no tiers.',
  'pricing.col.model': 'Model',
  'pricing.col.official': 'Official (CNY)',
  'pricing.col.ours': 'Your price (CNY)',
  'pricing.col.multiplier': 'Rate',
  'pricing.col.input': 'Input / 1M tokens',
  'pricing.col.output': 'Output / 1M tokens',
  'pricing.col.list': 'List price (USD)',
  'pricing.col.discount': 'Discount',
  'pricing.row.input': 'Input/M tokens',
  'pricing.row.output': 'Output/M tokens',
  'pricing.row.cache_read': 'Cache hit/M tokens',
  'pricing.row.cache_write': 'Cache write/M tokens',
  'pricing.row.not_billed': 'Not billed',
  'pricing.group.sku_count': '{count} models',
  'pricing.empty': 'Pricing data is temporarily unavailable — refresh again shortly.',
  'pricing.footnote':
    '✱ FX tracks the upstream published rate · Actual charge is the rate at request completion',

  // ── How it works ──────────────────────────────────────────────
  'how.eyebrow': 'Three steps to start',
  'how.title.lead': 'First request in ',
  'how.title.accent': '30 seconds',
  'how.step1.title': 'Create an account',
  'how.step1.body': 'Email sign-up. No invite codes, no card.',
  'how.step2.title': 'Mint an API key',
  'how.step2.body':
    'One click in the console. Revoke at any time; use multiple keys to attribute usage per project.',
  'how.step3.title': 'Wire up your app',
  'how.step3.body.before': 'Change the base URL to ',
  'how.step3.body.after': ", drop in your API key, and you're live.",

  // ── Stats band ────────────────────────────────────────────────
  'stats.users.label': 'Registered developers',
  'stats.users.caption': 'Across commerce, education, SaaS, content and more',
  'stats.requests.label': 'Requests · last 30 days',
  'stats.requests.caption': 'Median p50 latency 240ms · p95 820ms',
  'stats.uptime.label': '24h availability',
  'stats.uptime.caption_link': 'Live status →',

  // ── Status page ────────────────────────────────────────────────
  'status.eyebrow': 'Public status',
  'status.header.title': 'Service status',
  'status.title.lead': 'Service',
  'status.title.accent': 'status',
  'status.subtitle':
    'Availability grouped by namespace across the last 24 hours. Rates are request-weighted rollups over a rolling 24-hour window.',
  'status.summary.updated': 'Last updated {time}',
  'status.summary.updated_recent': 'Last updated {time} ({age})',
  'status.summary.updated_pending': 'Waiting for the first status snapshot.',
  'status.summary.window': 'Showing availability over the last {hours} hours, rolled up by namespace',
  'status.summary.overall': 'Overall',
  'status.summary.namespaces_inline': '{count} namespaces',
  'status.requests_24h': '24h requests {count}',
  'status.action.refresh': 'Refresh status',
  'status.state.healthy': 'Healthy',
  'status.state.busy': 'Busy',
  'status.state.unavailable': 'Unavailable',
  'status.empty.title': 'Namespace status is on its way',
  'status.empty.body': 'Namespace availability has not been published yet — refresh again shortly.',
  'status.events.eyebrow': 'Recent events',
  'status.events.title': 'Last 24 hours',
  'status.events.empty': 'No state transitions in the past 24 hours.',
  'status.events.view_all': 'View all events',
  'status.events.recovered': 'Availability recovered',
  'status.events.degraded': 'Availability degraded',
  'status.events.fluctuated': 'Brief fluctuation',
  'status.events.transition': '{channel} moved from {from} to {to}',
  'status.events.transition_to': '{channel} moved into {to}',
  'status.error.title': 'Status data is temporarily unavailable',
  'status.error.body': 'The public status endpoint did not return data just now. Please refresh and try again shortly.',

  // ── Closing CTA ───────────────────────────────────────────────
  'cta.eyebrow': 'Get started',
  'cta.title.lead': "Let's get your AI ",
  'cta.title.accent': 'running',
  'cta.subtitle': "Swap one base URL and you're in.",
  'cta.primary': 'Create free account',
  'cta.secondary': 'Read the docs',

  // ── Footer ────────────────────────────────────────────────────
  'footer.nav.aria_label': 'Footer',
  'footer.link.about': 'About',
  'footer.link.privacy': 'Privacy',
  'footer.link.terms': 'Terms',
  'footer.link.support': 'Support',

  // ── Locale switcher chrome ────────────────────────────────────
  'i18n.switcher.aria_label': 'Language',
};
